import { useState } from 'react';
import { ArrowRight, Car, Check, Clock, Heart, MapPin, Route, ShieldCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../utils/cart';
import { TRANSFER_TYPES } from '../data/transfers';
import { useCatalog } from '../data/catalog';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { PublicPartnerProducts } from '../components/PublicPartnerProducts';
import { PriceOnRequest } from '../components/PriceOnRequest';
import { RequestAvailabilityForm } from '../components/RequestAvailabilityForm';

const TransferCard = ({ tr, t, delay }) => {
  const [open, setOpen] = useState(false);
  const knownType = TRANSFER_TYPES.find((ty) => ty.id === tr.service_type);
  const serviceLabel = tr.service_type ? (knownType ? t(`tr.type.${tr.service_type}`) : tr.service_type) : '';
  const meta = [
    tr.distance && { icon: Route, label: tr.distance, testid: 'distance' },
    tr.duration && { icon: Clock, label: `${t('tr.approx')} ${tr.duration}`, testid: 'duration' },
    tr.vehicle && { icon: Car, label: tr.vehicle, testid: 'vehicle' },
  ].filter(Boolean);

  return (
    <Reveal delay={delay}>
      <div data-testid={`transfer-card-${tr.slug}`} className="rounded-2xl border border-eco-ink/10 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 self-center">
            {tr.img ? (
              <span className="hidden h-24 w-24 shrink-0 overflow-hidden rounded-2xl sm:block">
                <img src={tr.img} alt={tr.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.06]" data-testid={`transfer-img-${tr.slug}`} />
              </span>
            ) : (
              <span className="grid h-13 w-13 shrink-0 place-items-center rounded-2xl bg-eco-green p-3.5 text-eco-gold"><Car size={22} /></span>
            )}
            <div>
              {(tr.island || tr.country) && (
                <p className="text-[12px] font-semibold tracking-[0.14em] uppercase text-eco-gold" data-testid={`transfer-place-${tr.slug}`}>{[tr.island, tr.country].filter(Boolean).join(', ')}</p>
              )}
              <h2 className="eco-card-title mt-1" data-testid={`transfer-route-${tr.slug}`}>{tr.name}</h2>
              {meta.length > 0 && (
                <p className="eco-meta mt-2 flex flex-wrap items-center gap-4">
                  {meta.map((m) => (
                    <span key={m.testid} className="flex items-center gap-1" data-testid={`transfer-${m.testid}-${tr.slug}`}><m.icon size={12} /> {m.label}</span>
                  ))}
                </p>
              )}
              {tr.capacities?.length > 0 && (
                <p className="mt-2 flex flex-wrap gap-2" data-testid={`transfer-capacity-${tr.slug}`}>
                  {tr.capacities.map((c) => (
                    <span key={c} className="flex items-center gap-1 rounded-full bg-eco-cream px-3 py-1 text-xs font-medium text-eco-ink/65"><Users size={11} /> {c}</span>
                  ))}
                </p>
              )}
            </div>
          </div>
          <div className="w-full md:w-64">
            <PriceOnRequest compact testid={`transfer-price-${tr.slug}`} />
            <p className="mt-2 text-[11px] leading-relaxed text-eco-ink/50" data-testid={`transfer-price-note-${tr.slug}`}>{t('tr.price.note')}</p>
            <button
              data-testid={`transfer-request-${tr.slug}`}
              onClick={() => setOpen(!open)}
              className="eco-btn-primary mt-3 w-full"
            >
              {t('cta.transfer')} <ArrowRight size={14} />
            </button>
            <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-eco-ink/50" data-testid={`transfer-noprepay-${tr.slug}`}>
              <ShieldCheck size={13} className="mt-0.5 shrink-0 text-eco-green" /> {t('tr.noprepay')}
            </p>
            <button
              type="button"
              data-testid={`transfer-add-trip-${tr.slug}`}
              onClick={() => {
                const result = addToCart({ type: 'transfer', slug: tr.slug, name: tr.name, destination: [tr.island, tr.country].filter(Boolean).join(', ') });
                if (!result.added) toast(t('cart.duplicate'), { description: tr.name });
                else toast.success(t('cta.addtrip'), { description: tr.name });
              }}
              className="eco-btn-text mt-1 min-h-11 w-full justify-center"
            >
              <Heart size={14} /> {t('cta.addtrip')}
            </button>
          </div>
        </div>
        {tr.note && <p className="mt-4 text-xs text-eco-ink/45" data-testid={`transfer-note-${tr.slug}`}>{tr.note}</p>}
        <p className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-eco-green/80" data-testid={`transfer-localpartners-${tr.slug}`}>
          <MapPin size={12} /> {t('tr.localpartners')}
        </p>

        {open && (
          <div className="mt-6 border-t border-eco-ink/10 pt-6">
            <RequestAvailabilityForm
              testid={`transfer-form-${tr.slug}`}
              kind="transfer"
              destination={[tr.island, tr.country].filter(Boolean).join(', ')}
              product={tr.name}
              serviceType={serviceLabel}
              showPickup
              showDropoff
            />
          </div>
        )}
      </div>
    </Reveal>
  );
};

export default function TransfersPage() {
  const { t } = useLang();
  const { add: addToCart } = useCart();
  const { transfers, transfersLoaded } = useCatalog();
  useSEO('Transfers | Eco Collection', 'Transfers privados em Santiago, Cabo Verde, São Tomé e Príncipe — preço sob consulta.');

  return (
    <div data-testid="transfers-page">
      <PageHeader kicker={t('page.transfers.k')} title={t('page.transfers.t')} sub={t('page.transfers.s')} />
      <section className="bg-eco-cream py-14 md:py-20">
        <div className="mx-auto max-w-4xl space-y-6 px-5 md:px-8">
          {!transfersLoaded && (
            <div className="rounded-2xl border border-eco-ink/10 bg-white p-10 text-center text-sm text-eco-ink/50" data-testid="transfers-loading">…</div>
          )}
          {transfersLoaded && transfers.length === 0 && (
            <div className="rounded-2xl border border-dashed border-eco-ink/15 bg-white/60 p-10 text-center" data-testid="transfers-empty">
              <p className="text-sm text-eco-ink/55">{t('tr.empty')}</p>
            </div>
          )}
          {transfers.map((tr, i) => <TransferCard key={tr.slug} tr={tr} t={t} delay={i * 0.1} />)}

          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-dashed border-eco-ink/15 bg-white/60 p-6">
              <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-eco-ink/45">{t('tr.prep')}</h3>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {TRANSFER_TYPES.map((ty) => (
                  <span key={ty.id} data-testid={`transfer-type-${ty.id}`} className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium ${ty.ready ? 'bg-eco-green/10 text-eco-green' : 'bg-eco-ink/5 text-eco-ink/40'}`}>
                    {ty.ready && <Check size={12} />} {t(`tr.type.${ty.id}`)}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <PublicPartnerProducts type="transfer" />
        </div>
      </section>
    </div>
  );
}
